import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Building2,
    Mail,
    Phone,
    Globe,
    MapPin,
    Briefcase,
    Loader2,
    Check,
    AlertCircle,
    Save,
    User,
    Lock,
    KeyRound,
    ShieldCheck,
    Package,
    Layers,
    ChevronDown,
    ArrowLeft,
} from "lucide-react";
import Starfield from "./Starfield";
import Sidebar from "./company_Dashboard_sidebar";
import { growthosFetch } from "../config/api";

/* ==========================================================
   SETTINGS PAGE — tabbed: Company Profile / Account.
   Both tabs read the same `company` object shape used by
   company_home.js and the Sidebar (isfathena.company_registrations,
   via GET /growthos/auth/me).

   Endpoints confirmed against growthos_backend/main.py:
     PUT  /growthos/company-profile         — company identity/contact/address
     PUT  /growthos/account                 — admin_name
     POST /growthos/account/change-password — current + new password

   NOTE: admin_name is a NEW column — run this once against your DB
   before Save on the Account tab will persist anything (see main.py's
   own migration note above COMPANY_COLUMNS):
     ALTER TABLE company_registrations ADD COLUMN admin_name VARCHAR(200) DEFAULT '';
========================================================== */

const COMPANY_UPDATE_PATH = "/company-profile";
const ACCOUNT_UPDATE_PATH = "/account";
const PASSWORD_UPDATE_PATH = "/account/change-password";

const readStoredCompany = () => {
    try {
        const raw = localStorage.getItem("growthos_company");
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        return null;
    }
};

// Presence of this key means a TEAMMATE is logged in, not the owner —
// see login.js / company_Dashboard_sidebar.js for where it's set. The
// Access Control tab below is owner-only, so we use this to hide it
// for any teammate session (even an 'admin' one, per this feature's
// spec — only the founder/owner configures access here).
const readStoredMember = () => {
    try {
        const raw = localStorage.getItem("growthos_member");
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        return null;
    }
};

const writeStoredCompany = (partial) => {
    const merged = { ...readStoredCompany(), ...partial };
    localStorage.setItem("growthos_company", JSON.stringify(merged));
};

const Field = ({ label, icon: Icon, ...props }) => (
    <label className="block">
        <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
            {Icon && <Icon size={12} />} {label}
        </span>
        <input
            {...props}
            className="w-full rounded-xl border border-white/[0.08] bg-white/[0.03] px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none transition focus:border-orange-500/50 focus:bg-white/[0.05] disabled:opacity-50"
        />
    </label>
);

const StatusLine = ({ status }) => {
    if (!status) return <span />;
    return (
        <p
            className={`flex items-center gap-1.5 text-xs font-semibold ${
                status.type === "success" ? "text-emerald-400" : "text-red-400"
            }`}
        >
            {status.type === "success" ? <Check size={13} /> : <AlertCircle size={13} />}
            {status.message}
        </p>
    );
};

/* ---------------------------- Company Profile tab ---------------------------- */

const COMPANY_EMPTY_FORM = {
    company_name: "",
    industry_sector: "",
    company_type: "",
    email: "",
    contact_number: "",
    website: "",
    address_city: "",
    address_state: "",
    address_country: "",
};

const pickFields = (obj, template) =>
    Object.keys(template).reduce((acc, key) => {
        if (obj[key] !== undefined && obj[key] !== null) acc[key] = obj[key];
        return acc;
    }, {});

const CompanyProfileTab = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState(COMPANY_EMPTY_FORM);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);

    useEffect(() => {
        const stored = readStoredCompany();
        if (stored) setForm((f) => ({ ...f, ...pickFields(stored, COMPANY_EMPTY_FORM) }));

        (async () => {
            const { res, data } = await growthosFetch("/auth/me", { method: "GET" });
            if (res?.status === 401) {
                navigate("/login");
                return;
            }
            if (res?.ok && data?.success && data.data) {
                setForm((f) => ({ ...f, ...pickFields(data.data, COMPANY_EMPTY_FORM) }));
            }
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        if (status) setStatus(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setStatus(null);
        try {
            const { res, data } = await growthosFetch(COMPANY_UPDATE_PATH, {
                method: "PUT",
                body: JSON.stringify(form),
            });
            if (res?.status === 401) {
                navigate("/login");
                return;
            }
            if (res?.ok && data?.success) {
                setStatus({ type: "success", message: "Company profile updated." });
                writeStoredCompany(form);
            } else {
                setStatus({ type: "error", message: data?.message || "Couldn't save changes. Please try again." });
            }
        } catch (err) {
            setStatus({ type: "error", message: "Network error — please try again." });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
                <Loader2 size={14} className="animate-spin" /> Loading company info...
            </div>
        );
    }

    return (
        <form
            onSubmit={handleSave}
            className="relative overflow-hidden rounded-[24px] border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-2xl sm:p-8"
        >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />

            <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600">Basic Info</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Company Name" icon={Building2} value={form.company_name} onChange={handleChange("company_name")} placeholder="Acme Inc." />
                <Field label="Industry" icon={Briefcase} value={form.industry_sector} onChange={handleChange("industry_sector")} placeholder="e.g. SaaS, Retail" />
                <Field label="Company Type" value={form.company_type} onChange={handleChange("company_type")} placeholder="e.g. Private Ltd." />
            </div>

            <p className="mb-4 mt-7 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600">Contact</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Email Address" icon={Mail} type="email" value={form.email} onChange={handleChange("email")} placeholder="contact@company.com" />
                <Field label="Phone Number" icon={Phone} value={form.contact_number} onChange={handleChange("contact_number")} placeholder="+91 98765 43210" />
                <Field label="Website" icon={Globe} value={form.website} onChange={handleChange("website")} placeholder="https://company.com" />
            </div>

            <p className="mb-4 mt-7 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600">
                <MapPin size={12} className="mr-1 inline" /> Address
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field label="City" value={form.address_city} onChange={handleChange("address_city")} />
                <Field label="State" value={form.address_state} onChange={handleChange("address_state")} />
                <Field label="Country" value={form.address_country} onChange={handleChange("address_country")} />
            </div>

            <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.07] pt-6">
                <StatusLine status={status} />
                <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {saving ? "Saving..." : "Save Changes"}
                </button>
            </div>
        </form>
    );
};

/* ---------------------------- Account tab ---------------------------- */

const ACCOUNT_EMPTY_FORM = { admin_name: "" };
const PASSWORD_EMPTY_FORM = { current_password: "", new_password: "", confirm_password: "" };

const AccountTab = () => {
    const navigate = useNavigate();

    const [form, setForm] = useState(ACCOUNT_EMPTY_FORM);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);

    const [pwForm, setPwForm] = useState(PASSWORD_EMPTY_FORM);
    const [pwSaving, setPwSaving] = useState(false);
    const [pwStatus, setPwStatus] = useState(null);

    useEffect(() => {
        // admin_name lives on the same company_registrations row (new
        // column — see the migration note at the top of this file). Seed
        // from localStorage so this tab isn't empty; /auth/me refresh
        // below overwrites with server truth once it lands.
        const stored = readStoredCompany();
        if (stored) {
            setForm({ admin_name: stored.admin_name || "" });
        }

        (async () => {
            const { res, data } = await growthosFetch("/auth/me", { method: "GET" });
            if (res?.status === 401) {
                navigate("/login");
                return;
            }
            if (res?.ok && data?.success && data.data) {
                setForm({ admin_name: data.data.admin_name || "" });
            }
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        if (status) setStatus(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setStatus(null);
        try {
            const { res, data } = await growthosFetch(ACCOUNT_UPDATE_PATH, {
                method: "PUT",
                body: JSON.stringify(form),
            });
            if (res?.status === 401) {
                navigate("/login");
                return;
            }
            if (res?.ok && data?.success) {
                setStatus({ type: "success", message: "Account info updated." });
                writeStoredCompany(form);
            } else {
                setStatus({ type: "error", message: data?.message || "Couldn't save changes. Please try again." });
            }
        } catch (err) {
            setStatus({ type: "error", message: "Network error — please try again." });
        } finally {
            setSaving(false);
        }
    };

    const handlePwChange = (key) => (e) => {
        setPwForm((f) => ({ ...f, [key]: e.target.value }));
        if (pwStatus) setPwStatus(null);
    };

    const handlePwSubmit = async (e) => {
        e.preventDefault();
        if (pwForm.new_password.length < 8) {
            setPwStatus({ type: "error", message: "New password must be at least 8 characters." });
            return;
        }
        if (pwForm.new_password !== pwForm.confirm_password) {
            setPwStatus({ type: "error", message: "New password and confirmation don't match." });
            return;
        }
        setPwSaving(true);
        setPwStatus(null);
        try {
            const { res, data } = await growthosFetch(PASSWORD_UPDATE_PATH, {
                method: "POST",
                body: JSON.stringify({
                    current_password: pwForm.current_password,
                    new_password: pwForm.new_password,
                }),
            });
            if (res?.status === 401) {
                navigate("/login");
                return;
            }
            if (res?.ok && data?.success) {
                setPwStatus({ type: "success", message: "Password updated." });
                setPwForm(PASSWORD_EMPTY_FORM);
            } else {
                setPwStatus({ type: "error", message: data?.message || "Couldn't update password." });
            }
        } catch (err) {
            setPwStatus({ type: "error", message: "Network error — please try again." });
        } finally {
            setPwSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
                <Loader2 size={14} className="animate-spin" /> Loading account info...
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Profile info */}
            <form
                onSubmit={handleSave}
                className="relative overflow-hidden rounded-[24px] border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-2xl sm:p-8"
            >
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />
                <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600">Admin Profile</p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Full Name" icon={User} value={form.admin_name} onChange={handleChange("admin_name")} placeholder="Your name" />
                </div>
                <p className="mt-2 text-[11px] text-zinc-600">
                    Login email is managed from the Company Profile tab.
                </p>
                <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.07] pt-6">
                    <StatusLine status={status} />
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        {saving ? "Saving..." : "Save Changes"}
                    </button>
                </div>
            </form>

            {/* Password change */}
            <form
                onSubmit={handlePwSubmit}
                className="relative overflow-hidden rounded-[24px] border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-2xl sm:p-8"
            >
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />
                <p className="mb-4 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600">
                    <Lock size={12} /> Change Password
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field
                        label="Current Password"
                        icon={KeyRound}
                        type="password"
                        value={pwForm.current_password}
                        onChange={handlePwChange("current_password")}
                        placeholder="••••••••"
                        autoComplete="current-password"
                    />
                    <span className="hidden sm:block" />
                    <Field
                        label="New Password"
                        icon={KeyRound}
                        type="password"
                        value={pwForm.new_password}
                        onChange={handlePwChange("new_password")}
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                    />
                    <Field
                        label="Confirm New Password"
                        icon={KeyRound}
                        type="password"
                        value={pwForm.confirm_password}
                        onChange={handlePwChange("confirm_password")}
                        placeholder="Re-enter new password"
                        autoComplete="new-password"
                    />
                </div>
                <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.07] pt-6">
                    <StatusLine status={pwStatus} />
                    <button
                        type="submit"
                        disabled={pwSaving}
                        className="flex items-center gap-2 rounded-lg border border-orange-500/40 bg-orange-500/10 px-5 py-2.5 text-sm font-semibold text-orange-300 transition hover:bg-orange-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {pwSaving ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                        {pwSaving ? "Updating..." : "Update Password"}
                    </button>
                </div>
            </form>
        </div>
    );
};

/* ---------------------------- Access Control tab (owner only) ---------------------------- */
// Two layers, saved independently:
//   1. PRODUCTS — which products this teammate can open at all.
//        GET/PUT /team-members/{id}/product-access
//   2. MODULES  — which modules (sidebar entries of
//        company_product_dashboard.js) they see INSIDE one product.
//        GET /product-modules                                  — catalog
//        GET /team-members/{id}/module-access?product_id=N      — prefill
//        PUT /team-members/{id}/module-access                   — save
//
// Module access is per (member, product), so the product dropdown in
// layer 2 is scoped to whatever layer 1 currently grants — the owner
// can't hand out modules of a product the teammate can't even open.
// Section/card-level visibility inside a module is the next layer and
// isn't here yet.

const AccessControlTab = () => {
    const navigate = useNavigate();
    const [members, setMembers] = useState([]);
    const [products, setProducts] = useState([]);
    const [moduleCatalog, setModuleCatalog] = useState([]);
    const [loadingLists, setLoadingLists] = useState(true);

    const [selectedMemberId, setSelectedMemberId] = useState("");

    // Layer 1 — products
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [restricted, setRestricted] = useState(false);
    const [loadingAccess, setLoadingAccess] = useState(false);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);

    // Layer 2 — modules within one product
    // NOTE ON DIRECTION: the backend still speaks in terms of ALLOWED
    // modules (GET/PUT .../module-access send/receive the ids a teammate
    // MAY see). The checkboxes below show the opposite — ticked = HIDDEN
    // from that teammate — because that's the mental model the owner
    // actually uses ("hide these few, show the rest"). hiddenModuleIds is
    // purely a UI-side concept; it gets inverted against moduleCatalog
    // right before every read from / write to the backend, so the API
    // contract itself never changes.
    const [moduleProductId, setModuleProductId] = useState("");
    const [hiddenModuleIds, setHiddenModuleIds] = useState([]);
    const [moduleRestricted, setModuleRestricted] = useState(false);
    const [loadingModules, setLoadingModules] = useState(false);
    const [savingModules, setSavingModules] = useState(false);
    const [moduleStatus, setModuleStatus] = useState(null);

    useEffect(() => {
        (async () => {
            setLoadingLists(true);
            const [membersRes, productsRes, modulesRes] = await Promise.all([
                growthosFetch("/team-members", { method: "GET" }),
                growthosFetch("/products", { method: "GET" }),
                growthosFetch("/product-modules", { method: "GET" }),
            ]);
            if (
                membersRes.res?.status === 401 ||
                productsRes.res?.status === 401 ||
                modulesRes.res?.status === 401
            ) {
                navigate("/login");
                return;
            }
            if (membersRes.res?.ok && membersRes.data?.success) {
                setMembers(membersRes.data.data || []);
            }
            if (productsRes.res?.ok && productsRes.data?.success) {
                setProducts(productsRes.data.data || []);
            }
            if (modulesRes.res?.ok && modulesRes.data?.success) {
                setModuleCatalog(modulesRes.data.data || []);
            }
            setLoadingLists(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Switching teammate resets BOTH layers — a half-loaded module
        // list from the previous member would otherwise stay on screen
        // and look like it belongs to the new one.
        setModuleProductId("");
        setHiddenModuleIds([]);
        setModuleRestricted(false);
        setModuleStatus(null);

        if (!selectedMemberId) {
            setSelectedProductIds([]);
            setRestricted(false);
            return;
        }
        (async () => {
            setLoadingAccess(true);
            setStatus(null);
            const { res, data } = await growthosFetch(`/team-members/${selectedMemberId}/product-access`, {
                method: "GET",
            });
            if (res?.status === 401) {
                navigate("/login");
                return;
            }
            if (res?.ok && data?.success) {
                setSelectedProductIds(data.product_ids || []);
                setRestricted(!!data.restricted);
            }
            setLoadingAccess(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMemberId]);

    // Products the module picker may offer: everything while the
    // teammate is unrestricted, otherwise only what's ticked above. Read
    // off the LIVE tick state, so unticking a product immediately drops
    // it from the dropdown even before Save Access is pressed.
    const moduleEligibleProducts = restricted
        ? products.filter((p) => selectedProductIds.includes(p.id))
        : products;

    useEffect(() => {
        // The chosen product just lost access (unticked above) — drop it.
        if (moduleProductId && !moduleEligibleProducts.some((p) => String(p.id) === String(moduleProductId))) {
            setModuleProductId("");
            setHiddenModuleIds([]);
            setModuleRestricted(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restricted, selectedProductIds]);

    useEffect(() => {
        if (!selectedMemberId || !moduleProductId) {
            setHiddenModuleIds([]);
            setModuleRestricted(false);
            return;
        }
        (async () => {
            setLoadingModules(true);
            setModuleStatus(null);
            const { res, data } = await growthosFetch(
                `/team-members/${selectedMemberId}/module-access?product_id=${moduleProductId}`,
                { method: "GET" }
            );
            if (res?.status === 401) {
                navigate("/login");
                return;
            }
            if (res?.ok && data?.success) {
                // Backend sends the ALLOWED ids. Not restricted → nothing
                // is hidden. Restricted → hidden is "catalog minus allowed".
                const allowed = data.module_ids || [];
                setHiddenModuleIds(
                    data.restricted ? moduleCatalog.filter((m) => !allowed.includes(m.id)).map((m) => m.id) : []
                );
                setModuleRestricted(!!data.restricted);
            }
            setLoadingModules(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMemberId, moduleProductId, moduleCatalog]);

    const toggleProduct = (productId) => {
        setSelectedProductIds((ids) =>
            ids.includes(productId) ? ids.filter((id) => id !== productId) : [...ids, productId]
        );
        if (status) setStatus(null);
    };

    // Ticking a box here means "hide this module from the teammate" —
    // toggleModule just tracks membership in the hidden set; the
    // allowed/hidden inversion for the API happens only at load and save.
    const toggleModule = (moduleId) => {
        setHiddenModuleIds((ids) =>
            ids.includes(moduleId) ? ids.filter((id) => id !== moduleId) : [...ids, moduleId]
        );
        if (moduleStatus) setModuleStatus(null);
    };

    const handleSave = async () => {
        if (!selectedMemberId) return;
        setSaving(true);
        setStatus(null);
        try {
            const { res, data } = await growthosFetch(`/team-members/${selectedMemberId}/product-access`, {
                method: "PUT",
                body: JSON.stringify({ product_ids: selectedProductIds }),
            });
            if (res?.status === 401) {
                navigate("/login");
                return;
            }
            if (res?.ok && data?.success) {
                setStatus({ type: "success", message: "Product access updated." });
                setRestricted(true);
            } else {
                setStatus({ type: "error", message: data?.message || "Couldn't save changes. Please try again." });
            }
        } catch (err) {
            setStatus({ type: "error", message: "Network error — please try again." });
        } finally {
            setSaving(false);
        }
    };

    const handleSaveModules = async () => {
        if (!selectedMemberId || !moduleProductId) return;
        setSavingModules(true);
        setModuleStatus(null);
        try {
            // Invert here, right before the call: send the backend what
            // it actually expects — the ALLOWED ids — computed as
            // "everything in the catalog except what's ticked (hidden)".
            const allowedToSend = moduleCatalog
                .map((m) => m.id)
                .filter((id) => !hiddenModuleIds.includes(id));
            const { res, data } = await growthosFetch(`/team-members/${selectedMemberId}/module-access`, {
                method: "PUT",
                body: JSON.stringify({
                    product_id: Number(moduleProductId),
                    module_ids: allowedToSend,
                }),
            });
            if (res?.status === 401) {
                navigate("/login");
                return;
            }
            if (res?.ok && data?.success) {
                const allowedSaved = data.module_ids || allowedToSend;
                setHiddenModuleIds(moduleCatalog.filter((m) => !allowedSaved.includes(m.id)).map((m) => m.id));
                setModuleRestricted(true);
                setModuleStatus({ type: "success", message: "Module access updated." });
            } else {
                setModuleStatus({
                    type: "error",
                    message: data?.message || "Couldn't save changes. Please try again.",
                });
            }
        } catch (err) {
            setModuleStatus({ type: "error", message: "Network error — please try again." });
        } finally {
            setSavingModules(false);
        }
    };

    const selectedMember = members.find((m) => String(m.id) === String(selectedMemberId));
    const moduleProduct = products.find((p) => String(p.id) === String(moduleProductId));

    if (loadingLists) {
        return (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
                <Loader2 size={14} className="animate-spin" /> Loading team & products...
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* ─────────── Layer 1 — product access ─────────── */}
            <div className="relative overflow-hidden rounded-[24px] border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-2xl sm:p-8">
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />

                <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600">
                    <ShieldCheck size={12} /> Product Access
                </p>
                <p className="mb-5 text-xs text-zinc-500">
                    Choose a teammate, then pick which products they're allowed to open.
                    Leaving nobody configured means that teammate can see every product, as before.
                </p>

                {members.length === 0 ? (
                    <p className="text-sm text-zinc-500">No team members yet — invite someone from the Team page first.</p>
                ) : (
                    <>
                        {/* Member picker */}
                        <label className="block mb-5">
                            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                                <User size={12} /> Team Member
                            </span>
                            <div className="relative">
                                <select
                                    value={selectedMemberId}
                                    onChange={(e) => setSelectedMemberId(e.target.value)}
                                    className="w-full appearance-none rounded-xl border border-white/[0.08] bg-white/[0.03] px-3.5 py-2.5 pr-9 text-sm text-white outline-none transition focus:border-orange-500/50 focus:bg-white/[0.05]"
                                >
                                    <option value="" className="bg-[#111]">Select a team member…</option>
                                    {members.map((m) => (
                                        <option key={m.id} value={m.id} className="bg-[#111]">
                                            {m.name} — {m.email}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                            </div>
                        </label>

                        {selectedMemberId && (
                            <>
                                <p className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                                    <Package size={12} /> Products {selectedMember ? `for ${selectedMember.name}` : ""}
                                </p>

                                {loadingAccess ? (
                                    <div className="flex items-center gap-2 text-sm text-zinc-500">
                                        <Loader2 size={14} className="animate-spin" /> Loading access…
                                    </div>
                                ) : products.length === 0 ? (
                                    <p className="text-sm text-zinc-500">No products yet — add one from the Products page first.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {!restricted && (
                                            <p className="mb-1 text-[11px] text-orange-300/80">
                                                Not configured yet — this teammate currently sees every product.
                                            </p>
                                        )}
                                        {products.map((p) => (
                                            <label
                                                key={p.id}
                                                className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-2.5 transition hover:border-orange-500/30"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedProductIds.includes(p.id)}
                                                    onChange={() => toggleProduct(p.id)}
                                                    className="h-4 w-4 rounded border-white/20 bg-transparent accent-orange-500"
                                                />
                                                <span className="text-sm text-zinc-200">{p.product_name}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}

                                <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.07] pt-6">
                                    <StatusLine status={status} />
                                    <button
                                        type="button"
                                        onClick={handleSave}
                                        disabled={saving || loadingAccess}
                                        className="flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                        {saving ? "Saving..." : "Save Access"}
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* ─────────── Layer 2 — module access within one product ─────────── */}
            {selectedMemberId && (
                <div className="relative overflow-hidden rounded-[24px] border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-2xl sm:p-8">
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />

                    <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600">
                        <Layers size={12} /> Module Access
                    </p>
                    <p className="mb-5 text-xs text-zinc-500">
                        Pick one of {selectedMember ? `${selectedMember.name}'s` : "this teammate's"} products,
                        then choose which modules they see inside it. Saved separately per product — the same
                        teammate can have different modules on each.
                    </p>

                    {moduleEligibleProducts.length === 0 ? (
                        <p className="text-sm text-zinc-500">
                            This teammate has no products yet — grant one above and save first.
                        </p>
                    ) : (
                        <>
                            <label className="block mb-5">
                                <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                                    <Package size={12} /> Product
                                </span>
                                <div className="relative">
                                    <select
                                        value={moduleProductId}
                                        onChange={(e) => setModuleProductId(e.target.value)}
                                        className="w-full appearance-none rounded-xl border border-white/[0.08] bg-white/[0.03] px-3.5 py-2.5 pr-9 text-sm text-white outline-none transition focus:border-orange-500/50 focus:bg-white/[0.05]"
                                    >
                                        <option value="" className="bg-[#111]">Select a product…</option>
                                        {moduleEligibleProducts.map((p) => (
                                            <option key={p.id} value={p.id} className="bg-[#111]">
                                                {p.product_name}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                                </div>
                            </label>

                            {moduleProductId && (
                                <>
                                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                                            <Layers size={12} /> Modules {moduleProduct ? `in ${moduleProduct.product_name}` : ""}
                                        </p>
                                        {!loadingModules && moduleCatalog.length > 0 && (
                                            <div className="flex gap-3 text-[11px] font-semibold">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setHiddenModuleIds([]);
                                                        setModuleStatus(null);
                                                    }}
                                                    className="text-zinc-500 transition hover:text-orange-300"
                                                >
                                                    Show all
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setHiddenModuleIds(moduleCatalog.map((m) => m.id));
                                                        setModuleStatus(null);
                                                    }}
                                                    className="text-zinc-500 transition hover:text-orange-300"
                                                >
                                                    Hide all
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {loadingModules ? (
                                        <div className="flex items-center gap-2 text-sm text-zinc-500">
                                            <Loader2 size={14} className="animate-spin" /> Loading modules…
                                        </div>
                                    ) : moduleCatalog.length === 0 ? (
                                        <p className="text-sm text-zinc-500">No modules returned by the server.</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {!moduleRestricted && (
                                                <p className="mb-1 text-[11px] text-orange-300/80">
                                                    Not configured yet — this teammate currently sees every module of this product.
                                                </p>
                                            )}
                                            {moduleRestricted && hiddenModuleIds.length === moduleCatalog.length && (
                                                <p className="mb-1 text-[11px] text-red-400/90">
                                                    Everything's hidden — saving this leaves them with no modules at all in this product.
                                                </p>
                                            )}
                                            <p className="mb-2 text-[11px] text-zinc-500">
                                                Tick a module to hide it from this teammate. Leave it unticked to keep it visible.
                                            </p>
                                            <div className="grid gap-2 sm:grid-cols-2">
                                                {moduleCatalog.map((m) => (
                                                    <label
                                                        key={m.id}
                                                        className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-2.5 transition hover:border-orange-500/30"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={hiddenModuleIds.includes(m.id)}
                                                            onChange={() => toggleModule(m.id)}
                                                            className="h-4 w-4 rounded border-white/20 bg-transparent accent-orange-500"
                                                        />
                                                        <span className="text-sm text-zinc-200">{m.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-7 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.07] pt-6">
                                        <StatusLine status={moduleStatus} />
                                        <button
                                            type="button"
                                            onClick={handleSaveModules}
                                            disabled={savingModules || loadingModules}
                                            className="flex items-center gap-2 rounded-lg bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-500 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {savingModules ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                            {savingModules ? "Saving..." : "Save Modules"}
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

/* ---------------------------- Page shell (tabs) ---------------------------- */

const TABS = [
    { id: "company", label: "Company Profile", icon: Building2 },
    { id: "account", label: "Account", icon: User },
    { id: "access", label: "Access Control", icon: ShieldCheck },
];

const CompanySettings = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("company");
    // Owner = no growthos_member in localStorage (see readStoredMember
    // above / login.js). Access Control is owner-only, per spec.
    const isOwner = !readStoredMember();
    const visibleTabs = isOwner ? TABS : TABS.filter((t) => t.id !== "access");

    useEffect(() => {
        if (!localStorage.getItem("growthos_token")) {
            navigate("/login");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="relative min-h-screen w-full bg-[#050505] text-zinc-300">
            <Sidebar />

            <div className="relative min-h-screen w-full overflow-hidden lg:pl-64">
                <Starfield />

                <div className="relative z-10 mx-auto max-w-3xl space-y-6 px-4 py-10 sm:px-6 sm:py-14 lg:px-10">
                    <div className="-mb-2">
                        <button
                            type="button"
                            onClick={() => navigate("/company_home")}
                            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3.5 py-2 text-sm font-medium text-gray-300 transition hover:border-orange-600/40 hover:bg-orange-600/10 hover:text-orange-400"
                        >
                            <ArrowLeft size={15} /> Back
                        </button>
                    </div>
                    <div>
                        <h1 className="text-2xl font-extrabold text-white sm:text-3xl">Settings</h1>
                        <p className="mt-1 text-sm text-zinc-500">
                            Manage your company profile and account details.
                        </p>
                    </div>

                    {/* Tab bar */}
                    <div className="flex w-fit gap-1 rounded-xl border border-white/[0.08] bg-white/[0.02] p-1">
                        {visibleTabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                                    activeTab === tab.id
                                        ? "bg-orange-600 text-white"
                                        : "text-zinc-400 hover:text-white"
                                }`}
                            >
                                <tab.icon size={14} />
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {activeTab === "company" && <CompanyProfileTab />}
                    {activeTab === "account" && <AccountTab />}
                    {activeTab === "access" && isOwner && <AccessControlTab />}
                </div>
            </div>
        </div>
    );
};

export default CompanySettings;